/**
 * Sri Lanka billing for CyberSource capture-context + PA.
 * Sent in billTo server-side; UC does not show address fields when billingType is NONE.
 * - address1: never "N/A" — use city name (e.g. Colombo) when unknown
 * - administrativeArea: ISO 3166-2:LK subdivision (1 char), NOT legacy "WP"
 *   (CyberSource field max length 2; PA maps this to billAddrState)
 */

/** Legacy 2-letter labels → ISO 3166-2:LK province subdivision code. */
export const LK_LEGACY_TO_ISO3166_2 = {
  WP: '1',
  CP: '2',
  SP: '3',
  NO: '4',
  NW: '6',
  EP: '5',
  NC: '7',
  UP: '8',
  SG: '9',
};

export const LK_ISO3166_2_CODES = Object.values(LK_LEGACY_TO_ISO3166_2);

const LK_PROVINCE_BY_KEYWORD = [
  { iso: '1', keywords: ['western', 'colombo', 'gampaha', 'kalutara'] },
  { iso: '2', keywords: ['central', 'kandy', 'matale', 'nuwara'] },
  { iso: '3', keywords: ['southern', 'galle', 'matara', 'hambantota'] },
  { iso: '6', keywords: ['north western', 'kurunegala', 'puttalam'] },
  { iso: '5', keywords: ['eastern', 'batticaloa', 'trincomalee', 'ampara'] },
  { iso: '7', keywords: ['north central', 'anuradhapura', 'polonnaruwa'] },
  { iso: '8', keywords: ['uva', 'badulla', 'monaragala'] },
  { iso: '9', keywords: ['sabaragamuwa', 'ratnapura', 'kegalle'] },
  { iso: '4', keywords: ['northern', 'jaffna', 'kilinochchi', 'mannar', 'mullaitivu', 'vavuniya'] },
];

const PLACEHOLDER_VALUES = new Set(['n/a', 'na', '-', '.', 'none', 'null', 'tbd']);

export const DEFAULT_LK_BILLING = {
  address1: 'Colombo',
  locality: 'Colombo',
  administrativeArea: '1',
  postalCode: '00100',
  country: 'LK',
  buildingNumber: '1',
};

export function isRealBillingValue(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  return !PLACEHOLDER_VALUES.has(s.toLowerCase());
}

/** Normalize any province hint to ISO 3166-2:LK code (single digit 1–9). */
export function lkAdministrativeAreaForCybersource(administrativeArea, hintText = '') {
  const raw = String(administrativeArea ?? '').trim().toUpperCase();
  if (/^[1-9]$/.test(raw)) return raw;
  if (LK_LEGACY_TO_ISO3166_2[raw]) return LK_LEGACY_TO_ISO3166_2[raw];
  const haystack = `${raw} ${hintText}`.toLowerCase();
  for (const { iso, keywords } of LK_PROVINCE_BY_KEYWORD) {
    if (keywords.some((k) => haystack.includes(k))) return iso;
  }
  return DEFAULT_LK_BILLING.administrativeArea;
}

function postalFromColomboZone(text) {
  const m = String(text).match(/colombo\s*(\d{1,2})\b/i);
  if (!m) return null;
  const zone = m[1].padStart(2, '0');
  return `01${zone}0`.padEnd(5, '0').slice(0, 5);
}

export function parseBillingFromAddress(addressText, { provinceHint = '' } = {}) {
  const raw = String(addressText ?? '').trim();
  if (!isRealBillingValue(raw)) {
    return { ...DEFAULT_LK_BILLING };
  }

  const parts = raw.split(/[,;\n]+/).map((p) => p.trim()).filter(Boolean);
  const joined = parts.join(', ');
  const lower = joined.toLowerCase();

  let locality = DEFAULT_LK_BILLING.locality;
  if (lower.includes('colombo')) locality = 'Colombo';
  else if (parts.length > 1) {
    locality = parts[parts.length - 1].replace(/\d+/g, '').trim() || parts[parts.length - 1];
  } else locality = parts[0];

  const postalFromText = joined.match(/\b(\d{5})\b/);
  const postalCode =
    postalFromText?.[1] || postalFromColomboZone(joined) || DEFAULT_LK_BILLING.postalCode;

  const administrativeArea = lkAdministrativeAreaForCybersource('', provinceHint || joined);

  let address1 = parts[0] || joined;
  if (!isRealBillingValue(address1) || address1.toLowerCase() === 'n/a') {
    address1 = locality || DEFAULT_LK_BILLING.address1;
  }

  return {
    address1: address1.slice(0, 60),
    locality: (locality || DEFAULT_LK_BILLING.locality).slice(0, 50),
    administrativeArea,
    postalCode,
    country: 'LK',
    buildingNumber: '1',
  };
}

export function normalizeBillingFields({
  address = '',
  address1 = '',
  address2 = '',
  buildingNumber = '',
  locality = '',
  administrativeArea = '',
  administrative_area = '',
  postalCode = '',
  postal_code = '',
  country = 'LK',
  provinceHint = '',
} = {}) {
  const parsed = parseBillingFromAddress(isRealBillingValue(address) ? address : '', {
    provinceHint,
  });

  const adminRaw = administrativeArea || administrative_area;
  const admin = lkAdministrativeAreaForCybersource(
    isRealBillingValue(adminRaw) ? adminRaw : '',
    provinceHint || (isRealBillingValue(address) ? address : ''),
  );

  const postalRaw = postalCode || postal_code;
  const postal =
    isRealBillingValue(postalRaw) && /^\d{5}$/.test(String(postalRaw).trim())
      ? String(postalRaw).trim()
      : parsed.postalCode;

  const line1 = isRealBillingValue(address1) ? String(address1).trim() : parsed.address1;
  const city = isRealBillingValue(locality) ? String(locality).trim() : parsed.locality;

  return {
    address1: line1,
    ...(isRealBillingValue(address2) ? { address2: String(address2).trim() } : {}),
    buildingNumber: isRealBillingValue(buildingNumber) ? String(buildingNumber).trim() : '1',
    locality: city,
    administrativeArea: admin,
    postalCode: postal,
    country: String(country || 'LK').trim().toUpperCase() || 'LK',
  };
}

/** billTo address block for CyberSource APIs (always includes administrativeArea). */
export function formatBillToForCybersource(normalized = {}) {
  const country = String(normalized.country || 'LK').trim().toUpperCase() || 'LK';
  const billTo = {
    buildingNumber: String(normalized.buildingNumber || '1').trim() || '1',
    address1: String(normalized.address1 || DEFAULT_LK_BILLING.address1).trim(),
    locality: String(normalized.locality || DEFAULT_LK_BILLING.locality).trim(),
    postalCode: String(normalized.postalCode || DEFAULT_LK_BILLING.postalCode).trim(),
    country,
  };
  if (normalized.address2) billTo.address2 = String(normalized.address2).trim();

  if (country === 'US' || country === 'CA') {
    const admin = String(normalized.administrativeArea || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(admin)) billTo.administrativeArea = admin;
  } else if (country === 'LK') {
    billTo.administrativeArea = lkAdministrativeAreaForCybersource(normalized.administrativeArea);
  } else {
    const admin = String(normalized.administrativeArea || '').trim().slice(0, 2);
    if (admin) billTo.administrativeArea = admin;
  }

  return billTo;
}

export function billingFromRecord(record = {}) {
  const freeText =
    record.address ||
    record.student_address ||
    [record.address1, record.locality].filter(isRealBillingValue).join(', ');

  return normalizeBillingFields({
    address: freeText,
    address1: record.address1,
    address2: record.address2,
    buildingNumber: record.building_number || record.buildingNumber,
    locality: record.locality,
    administrativeArea: record.administrative_area || record.administrativeArea,
    postalCode: record.postal_code || record.postalCode,
    country: record.country,
    provinceHint: record.devisional_secretariat || record.provinceHint,
  });
}
